import { LitElement, html } from 'lit';
import { styles } from './css';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { imagebossUrl } from '@Helpers/imageboss';
import { logErrorToSentry } from '../../../../helpers/helpers';
import {
  fireAdobeAnalyticsEvent,
  adobeCleanString,
  analyticsCtaDataObject,
} from '../../../../helpers/adobe-analytics';

import '@Root/lib/lit-3form-block/components/cta-text';

import {
  SHARED_CLASSES,
  SHARED_ELEMENTS,
} from '@Root/lib/lit-3form-block/styles.js';

import { LIT_CSS_VARIABLES, GRID_LAYOUT } from '@Root/lib/lit-styles.js';

const getInit = { method: 'GET', headers: { Accept: 'application/json' } };

export class CustomizeMaterialBarrel extends LitElement {
  static get properties() {
    return {
      content: { type: Object },
      _loadedPages: { type: Object },
      _swatchesPerPage: { type: Number },
      _selectedProduct: { type: Object },
      _currentProductSwatches: { type: Array },
      _isLoadingCurrentResults: { type: Boolean },
      _hasLoadedInitialOptions: { type: Boolean },
      _loadedThreekitImages: { type: Object },
      _fallbackHardwareFinish: { type: Object },
    };
  }

  static get styles() {
    return [
      LIT_CSS_VARIABLES,
      SHARED_ELEMENTS,
      SHARED_CLASSES,
      GRID_LAYOUT,
      styles,
    ];
  }

  constructor() {
    super();

    this.content = JSON.parse(this.getAttribute('content'));

    this._swatchesPerPage = 24;
    this._totalResults = 0;
    this._lastFetchedPagePath = '';
    this._selectedProduct = {};
    this._allProductSwatches = [];
    this._currentProductSwatches = [];
    this._isLoadingCurrentResults = false;
    this._hasLoadedInitialOptions = false;
    this._fallbackHardwareFinish = { id: '10' };
    this._loadedPages = {};
    this._loadedThreekitImages = {};
  }

  async connectedCallback() {
    super.connectedCallback();

    // TODO: JESSE DESERVES A BETTER DEATH
    const jesseBlock = document.querySelector('jesse-block');
    if (jesseBlock) jesseBlock.remove();

    try {
      await this.fetchThreekitStages();
    } catch (error) {
      logErrorToSentry(error);
    }
  }

  render() {
    return this.showProductOptions
      ? html`
          <div class="barrel-wrapper print-none expanded">
            <div id="visualization-barrel-header" class="barrel-header">
              <span class="barrel-heavy-text">
                <span>EXPLORE APPLICATIONS</span>
              </span>
            </div>
            <p class="description header-description">
              Visualize this material integrated into some of our hardware
              solutions.
            </p>
            ${this.productOptionsHTML}
            ${this.hasSelectedProduct
              ? html`
                  <cta-button
                    cta-data=${this.secondaryCtaData}
                    cta-type="secondary"
                    class="cta-spacer"
                    .ctaAnalyticsData=${analyticsCtaDataObject(
                      this.secondaryCtaData,
                      'Explore Applications'
                    )}
                  ></cta-button>
                `
              : html`
                  <div class="cta-spacer"></div>
                `}
          </div>
        `
      : html``;
  }

  get productOptionsHTML() {
    return this._isLoadingCurrentResults
      ? html`
          <div class="no-results">
            <p class="description loading"></p>
          </div>
        `
      : html`
          <div class="product-options-wrapper">
            <div class="product-options-header">
              <p class="barrel-heavy-text">
                Hardware Solutions
              </p>
            </div>
            <div class="swatches">
              ${this._currentProductSwatches.map(option => {
                return this.renderOptionsSwatch(option);
              })}
            </div>
          </div>
        `;
  }

  renderOptionsSwatch(option) {
    if (option.image_src) {
      // Product Swatches
      return html`
        <div
          class="swatch-wrapper"
          @click="${() => this.updateSelectedProduct(option, true)}"
        >
          <span
            class=${classMap({
              'image-swatch': true,
              'selected-swatch': this.isSelectedProduct(option),
              'not-selected-swatch': !this.isSelectedProduct(option),
            })}
          >
            <img
              src=${imagebossUrl(option.image_src, { height: 250, width: 200 })}
            />
          </span>
          <p class="swatch-name">${this.configuredSwatchName(option)}</p>
        </div>
      `;
    }
  }

  configuredMaterialName(material) {
    return material.filter_option_text
      ? material.filter_option_text
      : material.slug[0].toUpperCase() + material.slug.slice(1);
  }

  configuredSwatchName(swatch) {
    if (swatch.display_name) {
      return swatch.display_name;
    } else {
      const name = swatch.name ? swatch.name : '';
      return swatch.parentName ? `${swatch.parentName} ${name}` : `${name}`;
    }
  }

  isSelectedProduct(option) {
    return (
      option.render_interlayer_id ===
        this._selectedProduct.render_interlayer_id &&
      option.image_src === this._selectedProduct.image_src
    );
  }

  get showProductOptions() {
    return this._currentProductSwatches?.length > 0;
  }

  updateSelectedProduct(option, sendAnalyticsEvent = false) {
    if (!option) return;
    option.threekit_stage_id = this.content.threekit_stage_id;
    this._selectedProduct = option;
    this.fetchThreekitRenderedImages();
    if (sendAnalyticsEvent) {
      const cleanDisplayName = adobeCleanString(option.display_name);
      const eventString = `Visualization|Select|Product-${cleanDisplayName}`;
      const eventData = {
        type: 'tool',
        detail: {
          eVar35: eventString,
        },
      };
      fireAdobeAnalyticsEvent(eventData);
    }
  }

  fetchThreekitRenderedImages() {
    const hardwareFinishId =
      this._selectedProduct.default_threekit_hardware_finish_id ||
      this._fallbackHardwareFinish.id;
    const params = {
      threekit_hardware_finish_id: hardwareFinishId,
      threekit_stage_id: this._selectedProduct.id,
      render_interlayer_id: this.content.render_interlayer_id,
      sample_type_id: this.content.sample_type_id,
    };

    // only pass defined values into search params
    const searchParams = new URLSearchParams(
      Object.entries(params).filter(([key, value]) => value !== undefined)
    );

    const layersUrl = `/threekit_layers?${searchParams}`;

    if (this._loadedThreekitImages[layersUrl]) {
      return this.addThreekitImages(this._loadedThreekitImages[layersUrl]);
    }

    this.dispatchFetchingCarouselImage();

    const init = {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };

    fetch(layersUrl, init)
      .then(response => {
        if (response.ok) {
          return response.json();
        }
      })
      .then(json => {
        if (!!json) {
          this._loadedThreekitImages[layersUrl] = json.threekit_layers;
          this.addThreekitImages(this._loadedThreekitImages[layersUrl]);
        }
      })
      .catch(error => {
        logErrorToSentry(error);
      });
  }

  dispatchFetchingCarouselImage() {
    const fetchingNewCarouselImage = new CustomEvent(
      'fetchingNewCarouselImage',
      {
        bubbles: true,
        composed: true,
      }
    );
    this.dispatchEvent(fetchingNewCarouselImage);
  }

  addThreekitImages(threekitLayers = []) {
    const renderedImages = [];

    threekitLayers.forEach((layer, index) => {
      const formattedLayer = [];
      layer.display_order = index;
      layer.rendered = true;
      layer.thumbnail_description = this.renderedImageThumbnailDescription;
      layer.productForMaterial = true;
      formattedLayer.push(layer.display_order.toString());
      formattedLayer.push(layer);
      renderedImages.push(formattedLayer[1]);
    });

    // dispatch renderedImages to Walter
    const productSelectedForMaterialEvent = new CustomEvent(
      'productSelectedForMaterial',
      {
        detail: {
          renderedImages,
        },
        bubbles: true,
        composed: true,
      }
    );
    this.dispatchEvent(productSelectedForMaterialEvent);
  }

  swatchBackgroundStyle(option) {
    const color = option.hex_code ? option.hex_code : option.color;
    return {
      'background-color': `#${color}`,
    };
  }

  updateCurrentProductSwatches() {
    this._currentProductSwatches = Object.values(
      this._loadedPages
    )[0]?.threekit_stages;
    this._isLoadingCurrentResults = false;
    if (!this._hasLoadedInitialOptions) {
      if (window.location.hash.includes('#visualize')) {
        this.updateSelectedProduct(this._currentProductSwatches[0]);
        document.querySelector('walter-block')?.scrollIntoView();
      }
      this._hasLoadedInitialOptions = true;
    }
  }

  async fetchThreekitStages() {
    this._isLoadingCurrentResults = true;
    const [basePath, baseQueryParams] = this.content.threekit_stages_path.split(
      '?'
    );

    const threekitParams = new URLSearchParams(baseQueryParams);
    threekitParams.set(
      'threekit_hardware_finish_id',
      this._fallbackHardwareFinish.id
    );
    threekitParams.set('sample_type_id', this.content.sample_type_id);

    let variantUrl = `${basePath}?${threekitParams}`;

    this._loadedPages = {};
    this._currentPagePath = variantUrl;

    await this.loadProductsPage(variantUrl || basePath)
      .then(json => {
        if (!!json) {
          this.setVariantOptions(json, variantUrl);
        }
        this._isLoadingCurrentResults = false;
      })
      .then(() => {
        this.updateCurrentProductSwatches();
      })
      .catch(error => {
        logErrorToSentry(error);
      });
  }

  loadProductsPage(productsPagePath) {
    return fetch(productsPagePath, getInit)
      .then(response => {
        if (response.ok) {
          return response.json();
        }
      })
      .catch(error => {
        logErrorToSentry(error);
      });
  }

  setVariantOptions(data, path) {
    this._loadedPages[path] = data;
  }

  get renderedImageThumbnailDescription() {
    const material = this.content.materialType
      ? `Material | ${this.content.materialType}`
      : '';
    const pattern = this.titleFromWalter
      ? `\nPattern | ${this.titleFromWalter}`
      : '';
    const hardwareSystem = this.hasSelectedProduct
      ? `\nHardware System | ${this._selectedProduct.display_name}`
      : '';
    const hardwareFinish = `\nHardware Finish | Clear Annodized`;
    return `${material}${pattern}${hardwareSystem}${hardwareFinish}`;
  }

  get hasSelectedProduct() {
    return !!Object.keys(this._selectedProduct).length;
  }

  get titleFromWalter() {
    return !!document.getElementsByTagName('walter-block')[0]
      ? JSON.parse(
          document
            .getElementsByTagName('walter-block')[0]
            .getAttribute('content')
        ).header
      : '';
  }

  get secondaryCtaData() {
    return JSON.stringify({
      text: 'Customize Solution',
      url: this.ctaDriverUrl,
      fullWidth: true,
    });
  }

  get ctaDriverUrl() {
    let driverUrl = '';
    if (this.hasSelectedProduct) {
      const searchParams = new URLSearchParams({
        render_interlayer_id: this.content.render_interlayer_id,
      });
      driverUrl = `${this._selectedProduct.path}?${searchParams}`;
    }
    return driverUrl;
  }
}
customElements.define('customize-material-barrel', CustomizeMaterialBarrel);
